import { LoadingSpinner } from '@components/Generic'
import axios from 'axios'
import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'

import { HowToContinue } from '@components/SwitchToMobile/steps/HowToContinue'
import { ScanQR } from '@components/SwitchToMobile/steps/ScanQR'
import { ShowPin } from '@components/SwitchToMobile/steps/ShowPin'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { WaitingForMobile } from '@components/SwitchToMobile/steps/WaitingForMobile'
import { API_URL, GOTOMOBILE_SERVICE_PATH } from '@constants/apiUrl'

export interface GetSwitchToMobileContextResponse {
  isFinishOnDesktopAfterPollingEnabled: boolean
  sms: {
    enabled: boolean
    maxAttempts: number
    retrySeconds: number
  }
  pinCode: {
    maxAttempts: number
  }
  endpoints: {
    reportDesktopCameraUrl: string
    stayOnDeviceUrl: string
    getProceedLinkUrl: string
    sendSmsUrl: string
    isMobileSessionStartedUrl: string
    closeDesktopSessionUrl: string
    startMobileSessionUrl: string
    closeMobileSessionUrl: string
    generatePinCodeUrl: string
    isPinCodeConsumedUrl: string
    verifyPinCodeUrl: string
    isUserJourneyFinishedUrl: string
  }
}

export const SwitchToMobileEnpalPage = observer(() => {
  const store = useContext(StoreContext)
  const [step, setStep] = useState(0)

  const [context, setContext] = useState<GetSwitchToMobileContextResponse | undefined>()

  const {
    generatePinCodeUrl,
    getProceedLinkUrl,
    isMobileSessionStartedUrl,
    isPinCodeConsumedUrl,
    sendSmsUrl,
    stayOnDeviceUrl,
    closeDesktopSessionUrl,
    isUserJourneyFinishedUrl,
  } = context?.endpoints || {}

  const [isCameraAvailable, setIsCameraAvailable] = useState(false)

  const checkIfUserHasWebcam = async (reportUrl: string) => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()

      const hasWebcam = devices.some((device) => device.kind === 'videoinput')

      setIsCameraAvailable(hasWebcam)

      await axios.post(
        `${reportUrl}`,
        { isDesktopCameraAvailable: true }, // uses hasWebcam originally. Turned off to show the generic flow of handling this via the browser and not decide for the user instead.
        {
          withCredentials: true,
        }
      )
    } catch (error) {
      devLog(error)
    }
  }

  const nextStep = () => {
    setStep(step + 1)
  }

  const initComponent = async () => {
    try {
      const { data } = await axios.get<GetSwitchToMobileContextResponse>(
        `${API_URL}/${GOTOMOBILE_SERVICE_PATH}/get-context`,
        {
          withCredentials: true,
        }
      )

      await checkIfUserHasWebcam(data.endpoints.reportDesktopCameraUrl)

      devLog(data)
      setContext(data)

      nextStep()
    } catch (error) {
      devLog(error)
    }
  }

  useEffect(() => {
    devLog('initComponent')
    void initComponent()
  }, [])

  const renderCurrentStep = () => {
    if (!context) return <LoadingSpinner />

    switch (step) {
      case 0:
        return <LoadingSpinner />
      case 1:
        return <HowToContinue nextStep={nextStep} stayUrl={stayOnDeviceUrl} />
      case 2:
        return (
          <ScanQR
            nextStep={nextStep}
            stayUrl={stayOnDeviceUrl}
            smsUrl={sendSmsUrl}
            smsMaxAttempts={context?.sms?.maxAttempts}
            isSmsEnabled={context?.sms?.enabled}
            isCameraAvailable={isCameraAvailable}
            retrySeconds={context?.sms?.retrySeconds}
            getProceedLinkUrl={getProceedLinkUrl}
            isMobileSessionStartedUrl={isMobileSessionStartedUrl}
            closeDesktopSessionUrl={closeDesktopSessionUrl}
          />
        )
      case 3:
        return (
          <ShowPin
            nextStep={nextStep}
            stayUrl={stayOnDeviceUrl}
            generatePinCodeUrl={generatePinCodeUrl}
            isPinCodeConsumedUrl={isPinCodeConsumedUrl}
            closeDesktopSessionUrl={closeDesktopSessionUrl}
            shouldProceedToPollingStep={context?.isFinishOnDesktopAfterPollingEnabled}
          />
        )

      case 4:
        return (
          <WaitingForMobile
            isUserJourneyFinishedUrl={isUserJourneyFinishedUrl}
            closeDesktopSessionUrl={closeDesktopSessionUrl}
          />
        )
      default:
        return <></>
    }
  }

  return (
    <div className="page-container mobile-m-0">
      {renderCurrentStep()}
      <div
        className="generic__test-proceed test-proceed"
        onClick={() => {
          if (stayOnDeviceUrl?.length) {
            forceRedirect(stayOnDeviceUrl)
          }
        }}
      >
        .
      </div>
    </div>
  )
})
